import { Page } from "../Components/Page";
import { FullPageSpinner } from "../Components/Spinner";
import { MigrationPage } from "../MigrationPage";
import { AmcLambdaFetch, awaitJSON } from "../utils/fetch-utils";
import { useSetError } from "../redux/modals";
import { useCompanyInfo } from "../redux/company";
import { useSelector } from "react-redux";
import { useExperimentFlag } from "../utils/experiment-context";
import * as UserRedux from "../redux/user";
import React, { useEffect, useMemo, useState } from "react";

// Every AMC Insights tab has migrated to tinuiti-frontend (DEV-23543) — the k8s gateway now
// routes every /amc-insights/<tab> URL there. This component's only remaining job is to force a
// real browser navigation (not a client-side reach-router route change, which would stay in this
// SPA and never reach the gateway):
//   - a URL that already names a tab (e.g. /amc-insights/geo) just needs a hard reload so the
//     gateway can take over.
//   - the bare /amc-insights path has no tab to reload into, so it first has to work out which
//     tab the company actually has access to (mirroring the old tab-availability logic) before
//     redirecting to it.

interface Report {
  reportType: string;
}

// Order matters: mirrors the old tab bar's left-to-right priority for picking a default when
// landing on the bare /amc-insights path with no tab specified.
const REPORT_GATED_TABS = ["geo", "media-mix", "new-to-brand", "pathing-analysis", "audience-penetration"];
const KNOWN_TABS = [...REPORT_GATED_TABS, "category-pathing", "tableau"];

const AMC_TEST_INSTANCE_ID = "amcgqfnthsb"; //Drip Drop for the testing

export default React.memo(({ "*": splat = "" }: { "*"?: string }): JSX.Element => {
  const setError = useSetError();
  const isAdmin = useSelector(UserRedux.isAdminSelector);
  const enableCategoryPathing = useExperimentFlag("enableAmcPathingAnalysis");
  const requestedTab = splat.split("/").filter(Boolean)[0];
  const isKnownTab = requestedTab && KNOWN_TABS.includes(requestedTab);

  let { cid, amc_instance_id: amcInstanceId } = useCompanyInfo();
  if (cid === "tinuititest") {
    amcInstanceId = AMC_TEST_INSTANCE_ID;
  }

  const [data, setData] = useState<Report[] | null>(null);

  useEffect(() => {
    if (isKnownTab || !amcInstanceId) {
      return;
    }
    const fetchAMCData = async () => {
      try {
        const response = await AmcLambdaFetch("/getAmcReports", {
          params: { amcInstanceId },
        });
        setData(await awaitJSON<Report[]>(response));
      } catch (error) {
        setError({
          title: "Error fetching AMC data",
          message: "There was an error fetching AMC data. Please try again later.",
        });
      }
    };
    fetchAMCData();
  }, [isKnownTab, amcInstanceId, setError]);

  // Same priority bpm's old tab bar used: first report-gated tab the company has, else Category
  // Pathing if the experiment's on, else Tableau for admins.
  const defaultTab = useMemo(() => {
    if (!data) {
      return undefined;
    }
    const reportTypes = data.map(report => report.reportType.replaceAll("_", "-"));
    const reportTab = REPORT_GATED_TABS.find(tab => reportTypes.includes(tab));
    if (reportTab) {
      return reportTab;
    }
    if (enableCategoryPathing) {
      return "category-pathing";
    }
    if (isAdmin) {
      return "tableau";
    }
    return undefined;
  }, [data, enableCategoryPathing, isAdmin]);

  useEffect(() => {
    if (isKnownTab || !defaultTab) {
      return;
    }
    const { pathname } = window.location;
    const basePath = pathname.slice(0, pathname.indexOf("amc-insights") + "amc-insights".length);
    window.location.assign(`${basePath}/${defaultTab}`);
  }, [isKnownTab, defaultTab]);

  // The URL already names a tab — no data needed, just hard-reload so the gateway routes it.
  // MigrationPage owns the reload (and its NODE_ENV === "development" guard, which throws
  // instead of reload-looping forever against a local dev server with no gateway to route to).
  if (isKnownTab) {
    return <MigrationPage title="AMC Insights" pageType="AMC Insights" />;
  }

  return (
    <Page app2Redesign pageType="AMC Insights" title="AMC Insights">
      <FullPageSpinner />
    </Page>
  );
});
